import {
    AtIcon,
    BoxIcon,
    CalendarIcon,
    CheckIcon,
    CircleInfoIcon,
    CollectionIcon,
    LockIcon,
    PhotosIcon,
    PinIcon,
    QuestionnaireIcon,
    StarIcon,
    UserIcon,
} from "@xola/icons";
import React from "react";
import { Permission, ProductObject } from "types";
import { AddOnList } from "../../components/Product/GlobalSettings/AddOns/AddOnList";
import { BlackoutRestriction } from "../../components/Product/GlobalSettings/BlackoutRestriction";
import { CheckInPreferences } from "../../components/Product/GlobalSettings/CheckInPreferences";
import { CheckoutPreferences } from "../../components/Product/GlobalSettings/CheckoutPreference/CheckoutPreferences";
import { CollectionList } from "../../components/Product/GlobalSettings/Collections/CollectionList";
import { CustomEmailFooter } from "../../components/Product/GlobalSettings/CustomEmailFooter";
import { DatePickerPreference } from "../../components/Product/GlobalSettings/DatePickerPreference/DatePickerPreference";
import { GlobalDemographics } from "../../components/Product/GlobalSettings/demographics/GlobalDemographics";
import { GlobalProductTerms } from "../../components/Product/GlobalSettings/GlobalProductTerms";
import { GlobalReviews } from "../../components/Product/GlobalSettings/GlobalReviews";
import { GlobalQuestionnaire } from "../../components/Product/Questionnaire/GlobalQuestionnaire";
import { useAuthorization } from "../../hooks/useAuthorization";
import { PickupLocationPage } from "@/components/Product/GlobalSettings/PickupLocationPage/PickupLocationPage";

export const useProductGlobalSettingTabs = () => {
    const { isGranted } = useAuthorization();

    /**
     * isGranted does not work here, it will check permission and return children or null. But when this gets rendered,
     * isGranted Children might not be run so the children of isGranted component will always render irrespective
     * of permission validity.
     */
    const showCheckInPreferenceTab = isGranted(Permission.PRODUCT_GLOBAL_SETTINGS_CHECKIN_PREFERENCE_TAB_VIEW);
    const showDatePickerTab = isGranted(Permission.PRODUCT_GLOBAL_SETTINGS_DATEPICKER_TAB_VIEW);
    const showReviewTab = isGranted(Permission.PRODUCT_GLOBAL_SETTINGS_REVIEW_TAB_VIEW);
    const showEmailFooterTab = isGranted(Permission.PRODUCT_GLOBAL_SETTINGS_EMAIL_FOOTER_TAB_VIEW);
    const showCollectionTab = isGranted(Permission.MODIFY_COLLECTIONS);

    const tabs = [
        {
            id: "blackout",
            label: "Blackouts and Restrictions",
            icon: <CircleInfoIcon />,
            description: "Define when your experiences cannot be reserved",
            content: <BlackoutRestriction />,
        },
        {
            id: "checkout",
            label: "Checkout Preferences",
            icon: <LockIcon size="large" />,
            description:
                "Customize the price-specific information that is displayed to your customers during checkout on your website.",
            content: <CheckoutPreferences />,
        },
        {
            id: "terms",
            label: "Global Terms and Conditions",
            icon: <PhotosIcon size="large" />,
            description:
                "Terms and Conditions that you want your guests to read and/or agree to before purchasing and coming for your experience.",
            content: <GlobalProductTerms />,
        },
        {
            id: "checkin",
            label: "Check-In Preferences",
            icon: <CheckIcon size="large" />,
            condition: showCheckInPreferenceTab,
            description:
                "Allows you to Check-In Guests when they arrive. Reviews and other follow on emails will not be sent to Guests who were not Checked In, if this feature is enabled",
            content: <CheckInPreferences />,
        },
        {
            id: "collections",
            label: "Collections",
            icon: <CollectionIcon size="large" />,
            condition: showCollectionTab,
            description: "Assemble your products into groups",
            content: <CollectionList />,
        },
        {
            id: "customer-email-footer",
            label: "Custom Email Footer",
            icon: <AtIcon size="large" />,
            condition: showEmailFooterTab,
            description:
                "Customize the footer of emails sent from within Xola (including both automated and manually sent emails).",
            content: <CustomEmailFooter />,
        },
        {
            id: "demographics",
            label: "Demographics",
            icon: <UserIcon size="large" />,
            description:
                "Customize the type of customers that will book your experience. (e.g. Adult, Children,  Senior, etc)",
            content: <GlobalDemographics />,
        },
        {
            id: "add-ons",
            label: "Add-Ons",
            icon: <BoxIcon size="large" />,
            description: "Additional items that you sell along with a reservation.",
            content: <AddOnList />,
        },
        {
            id: "pickup-locations",
            label: "Pickup Locations",
            icon: <PinIcon size="large" />,
            description: "Configure designated guest pickup locations.",
            content: <PickupLocationPage />,
        },

        {
            id: "datepicker",
            label: "Date Picker",
            icon: <CalendarIcon size="large" />,
            condition: showDatePickerTab,
            description: "This Datepicker appears in the Purchase Details section of all Xola checkout types",
            content: <DatePickerPreference />,
        },
        {
            id: "reviews",
            label: "Reviews",
            icon: <StarIcon size="large" />,
            condition: showReviewTab,
            description: "Set up emails that prompts customers to provide feedback on your services",
            content: <GlobalReviews />,
        },
        {
            id: "questionnaire",
            label: "Questionnaire",
            icon: <QuestionnaireIcon size="large" />,
            description: "Collect additional information from your customers in the online checkout or on Xola Waivers",
            content: <GlobalQuestionnaire productObject={ProductObject.EXPERIENCE} />,
        },
    ];

    return tabs;
};
